import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appServices } from '@/data/AppServices';
import {
  QuestionMode,
  VocabularyAssessment,
  type VocabularyQueue,
  type VocabularySessionView,
} from '@/domain/Models';

const emptySession: VocabularySessionView = {
  sessionId: '',
  status: 'IDLE',
  currentIndex: 0,
  totalCount: 0,
  questionMode: 'INTRODUCE' as VocabularySessionView['questionMode'],
  word: null,
  answerRevealed: false,
  hintRevealed: false,
  assessmentSubmitted: false,
  selectedAssessment: null,
  examples: [],
};

function assessmentLabel(a: string): string {
  if (a === VocabularyAssessment.UNFAMILIAR) return '陌生';
  if (a === VocabularyAssessment.FUZZY) return '模糊';
  return '掌握';
}

function modeLabel(mode: string): string {
  if (mode === QuestionMode.INTRODUCE) return '新词';
  if (mode === QuestionMode.EN2ZH) return '英译中';
  if (mode === QuestionMode.ZH2EN) return '中译英';
  if (mode === QuestionMode.LISTENING) return '听力';
  return '练习';
}

export function VocabularyPage() {
  const [queue, setQueue] = useState<VocabularyQueue>({
    newCount: 0,
    reviewCount: 0,
    totalCount: 0,
  });
  const [session, setSession] = useState<VocabularySessionView>(emptySession);
  const [studying, setStudying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ reviewed: 0, unfamiliar: 0, fuzzy: 0, mastered: 0 });
  const lastWordIdRef = useRef<string | null>(null);
  const lastRatedRef = useRef<string | null>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      setQueue(await appServices.vocabulary.todayQueue());
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
    void start();
  }, [refresh]);

  // 词卡切换时按设置自动播放词汇音频 (对齐 Android autoPlayVocabularyAudio)
  useEffect(() => {
    if (!studying || !session.word) return;
    if (lastWordIdRef.current === session.word.id) return;
    lastWordIdRef.current = session.word.id;
    void (async () => {
      const s = await appServices.settings.get();
      if (s.autoPlayVocabularyAudio && session.word?.wordAudioAssetId) {
        await appServices.audio.play(session.word.wordAudioAssetId, s.playbackSpeed);
      }
    })();
  }, [session.word, studying]);

  async function start() {
    try {
      setBusy(true);
      setFinished(false);
      const id = await appServices.vocabulary.startOrResumeSession();
      setSession(await appServices.vocabulary.session(id));
      setStudying(true);
    } catch (e) {
      setError(String(e).replace(/^Error:\s*/, ''));
    } finally {
      setBusy(false);
    }
  }

  async function reloadSession() {
    const view = await appServices.vocabulary.session(session.sessionId);
    if (view.status === 'COMPLETED') {
      setStudying(false);
      setFinished(true);
      await refresh();
    } else {
      setSession(view);
    }
  }

  async function submit(assessment: VocabularyAssessment) {
    try {
      if (lastRatedRef.current !== session.word?.id) {
        lastRatedRef.current = session.word?.id ?? null;
        setStats((s) => ({
          reviewed: s.reviewed + 1,
          unfamiliar: s.unfamiliar + (assessment === VocabularyAssessment.UNFAMILIAR ? 1 : 0),
          fuzzy: s.fuzzy + (assessment === VocabularyAssessment.FUZZY ? 1 : 0),
          mastered: s.mastered + (assessment === VocabularyAssessment.MASTERED ? 1 : 0),
        }));
      }
      await appServices.vocabulary.submitAssessment(session.sessionId, assessment);
      await reloadSession();
    } catch (e) {
      setError(String(e).replace(/^Error:\s*/, ''));
    }
  }

  async function reveal() {
    await appServices.vocabulary.revealAnswer(session.sessionId);
    await reloadSession();
  }

  async function next() {
    await appServices.vocabulary.advanceToNext(session.sessionId);
    await reloadSession();
  }

  async function speak(assetId: string | undefined | null) {
    if (!assetId) return;
    const s = await appServices.settings.get();
    await appServices.audio.play(assetId, s.playbackSpeed);
  }

  // ---- 练习进行中 ----
  if (studying && session.status === 'IN_PROGRESS') {
    const w = session.word;
    const isLast = session.currentIndex + 1 >= session.totalCount;
    return (
      <div className="page stack">
        <div className="muted">
          第 {session.currentIndex + 1} 条 · 剩余 {Math.max(0, session.totalCount - session.currentIndex - 1)} ·{' '}
          {modeLabel(session.questionMode)}
        </div>

        {w && (
          <div className="card stack">
            {(session.answerRevealed || session.questionMode === QuestionMode.INTRODUCE || session.questionMode === QuestionMode.EN2ZH || session.questionMode === QuestionMode.LISTENING) && (
              <strong style={{ fontSize: '1.6rem' }}>{w.term}</strong>
            )}
            {session.answerRevealed && w.ipa && <div className="muted">{w.ipa}</div>}
            {(session.answerRevealed || session.questionMode === QuestionMode.INTRODUCE || session.questionMode === QuestionMode.ZH2EN) && (
              <div style={{ fontSize: '1.15rem' }}>{w.chineseGloss}</div>
            )}
            {!session.answerRevealed && session.questionMode !== QuestionMode.INTRODUCE && (
              <button type="button" onClick={() => void reveal()}>
                显示答案
              </button>
            )}
            {(session.answerRevealed || session.questionMode === QuestionMode.INTRODUCE) && (
              <div className="row">
                <button className="secondary" type="button" onClick={() => void speak(w.wordAudioAssetId)}>
                  朗读
                </button>
              </div>
            )}
          </div>
        )}

        {w &&
          (session.answerRevealed || session.questionMode === QuestionMode.INTRODUCE) &&
          session.examples.map((ex, i) => (
            <div className="card stack" key={i}>
              <div>{ex.textEn}</div>
              {ex.textZh && <div className="muted">{ex.textZh}</div>}
              <div className="row">
                <button
                  className="secondary"
                  type="button"
                  onClick={() => void speak(ex.audioAssetId)}
                >
                  朗读例句
                </button>
              </div>
            </div>
          ))}

        {(session.answerRevealed || session.questionMode === QuestionMode.INTRODUCE) && (
          session.assessmentSubmitted ? (
          <div className="row">
            <span className="muted">已选择：{assessmentLabel(session.selectedAssessment ?? '')}</span>
            <button type="button" style={{ flex: 1 }} onClick={() => void next()}>
              {isLast ? '完成本次学习' : '下一条'}
            </button>
          </div>
        ) : (
          <div className="grid-3">
            <button
              type="button"
              className="secondary"
              onClick={() => void submit(VocabularyAssessment.UNFAMILIAR)}
            >
              陌生
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => void submit(VocabularyAssessment.FUZZY)}
            >
              模糊
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => void submit(VocabularyAssessment.MASTERED)}
            >
              掌握
            </button>
          </div>
        )
        )}
        {error && <div className="error-box">{error}</div>}
      </div>
    );
  }

  // ---- 完成页 ----
  if (finished) {
    return (
      <div className="page stack" style={{ justifyContent: 'center', minHeight: '70vh' }}>
        <h2 style={{ margin: 0 }}>本次学习完成</h2>
        {stats.reviewed > 0 && (
          <div className="muted">
            共评价 {stats.reviewed} 条 · 陌生 {stats.unfamiliar} · 模糊 {stats.fuzzy} · 掌握 {stats.mastered}
          </div>
        )}
        <button type="button" onClick={() => navigate('/vocabulary')}>
          返回
        </button>
      </div>
    );
  }

  // ---- 今日词汇首页式 ----
  return (
    <div className="page stack">
      <h2 style={{ margin: 0 }}>今日词汇</h2>
      <div className="muted">
        新内容 {queue.newCount} 条 · 到期 {queue.reviewCount} 条
      </div>
      {busy ? (
        <div className="muted">正在准备…</div>
      ) : queue.totalCount > 0 ? (
        <button type="button" onClick={() => void start()}>
          开始背单词
        </button>
      ) : (
        <div className="muted">今天的自动学习任务已完成</div>
      )}
      {error && <div className="error-box">{error}</div>}
    </div>
  );
}
